import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, FileDown, Send, Search, Truck } from "lucide-react";
import { adminApi, adminUrl, money, fmtDate } from "../lib/api";
import { Card, Title, Badge, Modal, STATUS, act } from "./kit";
import { toast } from "sonner";

function Tracking({ o, onChanged }) {
  const [num, setNum] = useState(o.tracking_number || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await act(() => adminApi.post(`/orders/${o.id}/tracking`, { number: num }));
      data.emailed ? toast.success("Číslo zásilky uloženo a odesláno zákazníkovi") : toast.warning("Uloženo, ale e-mail se nepodařilo odeslat (zkontrolujte nastavení e-mailů)");
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[var(--teal-soft)]" data-testid="order-tracking">
      <p className="text-[var(--muted)] mb-1">Číslo zásilky {o.tracking_number && <span className="text-[var(--teal)] font-bold">· odesláno {fmtDate(o.shipped_at)}</span>}</p>
      <div className="flex gap-2">
        <input className="field !py-2" value={num} onChange={(e) => setNum(e.target.value)} placeholder={o.shipping.carrier === "zasilkovna" ? "např. Z1234567890" : "např. DR1234567890C"} data-testid="order-tracking-input" />
        <button onClick={save} disabled={busy || num.trim().length < 5} className="btn-pink !px-4 whitespace-nowrap disabled:opacity-50" data-testid="order-tracking-send-btn"><Truck className="w-4 h-4" /> {o.tracking_number ? "Poslat znovu" : "Uložit a poslat"}</button>
      </div>
      {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-xs underline mt-1 inline-block" data-testid="order-tracking-link">Otevřít sledování zásilky</a>}
    </div>
  );
}

function Detail({ o, onChanged }) {
  const paid = async () => {
    if (!window.confirm("Potvrdit přijetí platby? Zákazníkovi se odešle faktura a odemknou se soubory.")) return;
    await act(() => adminApi.post(`/orders/${o.id}/mark-paid`), "Platba potvrzena, faktura odeslána");
    onChanged();
  };
  const cancel = async () => {
    if (!window.confirm("Zrušit objednávku?")) return;
    await act(() => adminApi.post(`/orders/${o.id}/cancel`), "Zrušeno");
    onChanged();
  };
  const b = o.billing || {};
  return (
    <div className="space-y-4" data-testid="order-detail-modal">
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div><p className="text-[var(--muted)]">Zákazník</p><p className="font-bold">{o.name || "—"}</p><p>{o.email}</p><p>{[b.company, b.street, b.zip, b.city].filter(Boolean).join(", ")}</p>{b.ico && <p>IČO {b.ico}</p>}</div>
        <div><p className="text-[var(--muted)]">Platba</p><p className="font-bold">{{ bank: "Převod / QR", comgate: "Comgate", free: "Bez úhrady" }[o.payment_method]}</p><p>VS {o.number}</p>{o.invoice_number && <p>Faktura {o.invoice_number}</p>}{o.voucher_codes?.length > 0 && <p>Vytvořené poukazy: {o.voucher_codes.join(", ")}</p>}</div>
      </div>
      {o.shipping && (
        <div className="rounded-2xl border-2 border-[var(--teal-soft)] p-4 text-sm" data-testid="order-shipping-info">
          <p className="text-[var(--muted)]">Doprava</p>
          <p className="font-bold">{o.shipping.carrier_name} · {o.shipping.mode_name}</p>
          <p>{[o.shipping.name, o.shipping.street, o.shipping.zip, o.shipping.city].filter(Boolean).join(", ")}{o.shipping.phone && ` · ${o.shipping.phone}`}</p>
          {o.shipping.mode !== "address" && <p className="text-xs text-[var(--muted)] mt-1">Odeslat na nejbližší dostupné {o.shipping.mode === "zbox" ? "Z-BOX" : "výdejní místo"} podle adresy.</p>}
          {o.shipping.note && <p className="mt-1">Poznámka: {o.shipping.note}</p>}
          {o.status !== "cancelled" && <Tracking o={o} onChanged={onChanged} />}
        </div>
      )}
      <div className="rounded-2xl bg-[var(--blush)] p-4 text-sm space-y-1">
        {o.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.name}{i.qty > 1 && ` × ${i.qty}`}</span><span>{money(i.price * (i.qty || 1))}</span></div>)}
        {o.discount_amount > 0 && <div className="flex justify-between"><span>Sleva {o.discount_code}</span><span>−{money(o.discount_amount)}</span></div>}
        {o.voucher_amount > 0 && <div className="flex justify-between"><span>Poukaz {o.voucher_code}</span><span>−{money(o.voucher_amount)}</span></div>}
        <div className="flex justify-between font-extrabold text-base pt-2"><span>Celkem</span><span>{money(o.total)}</span></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {o.status === "pending" && <button onClick={paid} className="btn-pink" data-testid="order-mark-paid-btn"><CheckCircle2 className="w-4 h-4" /> Potvrdit přijetí platby</button>}
        {o.status === "pending" && <button onClick={cancel} className="btn-ghost" data-testid="order-cancel-btn"><XCircle className="w-4 h-4" /> Zrušit</button>}
        <a href={adminUrl(`/orders/${o.id}/invoice.pdf`)} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="order-invoice-pdf-btn"><FileDown className="w-4 h-4" /> {o.invoice_number ? "Faktura PDF" : "Náhled faktury"}</a>
        {o.status === "paid" && <button onClick={() => act(() => adminApi.post(`/orders/${o.id}/resend-invoice`), "Faktura znovu odeslána")} className="btn-ghost" data-testid="order-resend-btn"><Send className="w-4 h-4" /> Znovu poslat fakturu</button>}
      </div>
    </div>
  );
}

export default function Orders() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = () => adminApi.get(`/orders?${new URLSearchParams({ ...(status && { status }), ...(q && { q }) })}`).then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, [status]); // eslint-disable-line
  return (
    <div data-testid="admin-orders">
      <Title>Objednávky</Title>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["", "Vše"], ["pending", "Čeká na platbu"], ["paid", "Zaplaceno"], ["cancelled", "Zrušeno"]].map(([v, l]) => <button key={v} onClick={() => setStatus(v)} data-testid={`orders-filter-${v || "all"}`} className={`px-4 py-2 rounded-full text-sm font-bold ${status === v ? "bg-[var(--pink)] text-white" : "bg-white border border-[#f1e4ea]"}`}>{l}</button>)}
        <div className="relative ml-auto"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" /><input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Číslo / e-mail + Enter" className="field !pl-9 !py-2" data-testid="orders-search-input" /></div>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <Card className="text-sm text-[var(--muted)]">Žádné objednávky.</Card>}
        {list.map((o) => (
          <Card key={o.id} className="!p-4 flex flex-wrap items-center gap-4 cursor-pointer hover:border-[var(--pink)] transition-colors" onClick={() => setSel(o)} data-testid={`admin-order-${o.number}`}>
            <div className="flex-1 min-w-[180px]"><p className="font-bold">{o.number}</p><p className="text-xs text-[var(--muted)]">{fmtDate(o.created_at)} · {o.email}</p></div>
            <Badge tone={STATUS[o.status][1]}>{STATUS[o.status][0]}</Badge>
            <span className="text-sm">{{ bank: "Převod", comgate: "Karta", free: "Zdarma" }[o.payment_method]}</span>
            <span className="font-extrabold w-24 text-right">{money(o.total)}</span>
          </Card>
        ))}
      </div>
      <Modal open={!!sel} onClose={() => setSel(null)} title={`Objednávka ${sel?.number || ""}`}>
        {sel && <Detail o={sel} onChanged={() => { setSel(null); load(); }} />}
      </Modal>
    </div>
  );
}
